import { NhlSeasonStats } from '../../lib/api/nhlPlayers';
import {
  CONTRACT_VALUE_BADGE_CONFIG,
  computeProjectedProduction,
  computeValueLabelFromSeasonStats,
} from '../../lib/contractValue';
import { translate } from '../../lib/translations';

interface ContractInfo {
  salary: number;
  yearleft: string;
}

interface ContractValueBadgeProps {
  contractInfo: ContractInfo | null;
  seasonStats: NhlSeasonStats | null;
  isGoalie: boolean;
  language: 'fr' | 'en';
}

type ValueLabel = 'underpaid' | 'fair' | 'overpaid' | 'unknown';

export function ContractValueBadge({ contractInfo, seasonStats, isGoalie, language }: ContractValueBadgeProps) {
  if (!contractInfo || !contractInfo.salary) {
    return null;
  }

  const label: ValueLabel = computeValueLabelFromSeasonStats(contractInfo.salary, seasonStats, isGoalie);
  const { bg, textKey } = CONTRACT_VALUE_BADGE_CONFIG[label];

  // Compute detail text
  let detailText = '';
  if (label !== 'unknown' && seasonStats?.seasonTotals?.length) {
    const regularNhl = seasonStats.seasonTotals
      .filter((s) => s.gameTypeId === 2 && s.leagueAbbrev === 'NHL')
      .sort((a, b) => b.season - a.season);

    const latest = regularNhl[0];
    if (latest) {
      if (isGoalie) {
        const projectedWins = computeProjectedProduction(latest.wins, latest.gamesPlayed);
        const perWin = projectedWins && projectedWins > 0
          ? Math.round(contractInfo.salary / projectedWins).toLocaleString()
          : null;
        detailText = perWin
          ? `$${perWin} / ${translate('stat.wins', language).toLowerCase()}`
          : '';
      } else {
        const projectedPoints = computeProjectedProduction(latest.points, latest.gamesPlayed);
        const perPoint = projectedPoints && projectedPoints > 0
          ? Math.round(contractInfo.salary / projectedPoints).toLocaleString()
          : null;
        detailText = perPoint
          ? `$${perPoint} / ${translate('stat.points', language).toLowerCase()}`
          : '';
      }
    }
  }

  return (
    <div className="d-flex align-items-center gap-2 flex-wrap">
      <span className={`badge ${bg} px-3 py-2 fs-6`}>
        {translate(textKey, language)}
      </span>
      {detailText && (
        <span className="text-muted small">({detailText})</span>
      )}
    </div>
  );
}
